import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

/**
 * This hook is the single source of truth for client-side action permissions.
 * Every check below is keyed off `activeOrgId`, which AuthContext guarantees
 * is always an org the signed-in user is actually assigned to (see
 * AuthContext.setActiveOrgId — it refuses to set an org the user has no
 * user_assignments row for). Do not build a separate permission matrix
 * elsewhere that re-derives org/building access from raw IDs — extend
 * PmPermissions and usePmPermissionCheck instead. Note this is a UX
 * convenience layer only: RLS (supabase/rls_policies.sql, is_admin_of /
 * is_strata_admin_of) is the actual last line of defense and enforces the
 * same membership rule independently of anything checked here.
 */

export interface PmPermissions {
  manage_units: boolean;
  manage_hosts: boolean;
  manage_bookings: boolean;
  manage_compliance: boolean;
  manage_disputes: boolean;
  manage_fines: boolean;
  view_reports: boolean;
  manage_amenities: boolean;
  approve_amenity_bookings: boolean;
  manage_maintenance: boolean;
  manage_documents: boolean;
  view_audit_log: boolean;
}

export const DEFAULT_PM_PERMISSIONS: PmPermissions = {
  manage_units: true,
  manage_hosts: true,
  manage_bookings: true,
  manage_compliance: true,
  manage_disputes: true,
  manage_fines: true,
  view_reports: true,
  manage_amenities: true,
  approve_amenity_bookings: true,
  manage_maintenance: true,
  manage_documents: true,
  view_audit_log: true,
};

export function usePmPermissions() {
  const { activeOrgId, user } = useAuth();
  return useQuery({
    queryKey: ["pm_permissions", activeOrgId],
    // Only query for property_manager role — other roles (host, strata_admin, etc.)
    // don't have pm_permissions and always get full access via usePmPermissionCheck.
    enabled: !!activeOrgId && user?.role === "property_manager",
    staleTime: 1000 * 60 * 5, // 5 min cache — doesn't change often
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("pm_permissions")
        .eq("id", activeOrgId!)
        .single();
      if (error) throw error;
      // Merge with defaults so new permissions keys are always defined
      return { ...DEFAULT_PM_PERMISSIONS, ...(data?.pm_permissions ?? {}) } as PmPermissions;
    },
  });
}

export function useUpdatePmPermissions() {
  const queryClient = useQueryClient();
  const { activeOrgId } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (permissions: PmPermissions) => {
      if (!activeOrgId) throw new Error("No active organization");
      const { error } = await supabase
        .from("organizations")
        .update({ pm_permissions: permissions })
        .eq("id", activeOrgId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pm_permissions", activeOrgId] });
      toast({ title: "PM permissions updated", description: "Property Manager access settings saved." });
    },
    onError: (e: Error) =>
      toast({ title: "Error saving permissions", description: e.message, variant: "destructive" }),
  });
}

/**
 * Returns a function that checks a single PM permission.
 * - For non-PM roles (strata_admin, system_admin): always returns true.
 * - For PM users: returns the stored permission value (defaults to true if not set).
 */
export function usePmPermissionCheck() {
  const { user } = useAuth();
  const { data: permissions } = usePmPermissions();

  return (key: keyof PmPermissions): boolean => {
    if (!user) return false;
    if (user.role !== "property_manager") return true;
    return permissions?.[key] ?? DEFAULT_PM_PERMISSIONS[key];
  };
}
